import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { type EntityDetailsPatch, useOnboarding } from '../../context/OnboardingContext'
import { Field, Notice, StateSelect, StepCard, StepTitle, UploadButton } from '../../components/ui'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { createEmptyEntity } from '../../lib/factories'
import { entityDetailsMissing } from '../../lib/completion'
import { ENTITY_IMPORT_FIELDS, downloadCsvTemplate, readImportFile } from '../../lib/importRecords'
import type { BusinessEntity } from '../../types/onboarding'

const headCellSx = { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', px: 1, py: 1.25, whiteSpace: 'nowrap' } as const
const cellSx = { px: 1, py: 1, verticalAlign: 'top' } as const

const SOURCE_LABELS: Record<BusinessEntity['addedBy'], string> = {
  MISC: 'Added by MISC',
  Client: 'Added manually',
  Import: 'Imported',
}

/** Design manual p.4 — Business Entity Setup. */
export function StepEntitySetup() {
  const { state, dispatch, showBlockers } = useOnboarding()
  const [importResult, setImportResult] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null)
  const [showIssues, setShowIssues] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState<BusinessEntity | null>(null)

  const update = (entityId: string, patch: EntityDetailsPatch) =>
    dispatch({ type: 'UPDATE_ENTITY', entityId, patch })

  const onImport = async (files: File[]) => {
    try {
      const records = await readImportFile(files[0], ENTITY_IMPORT_FIELDS, 'entity')
      const entities = records
        .filter((r) => Object.values(r).some((v) => v.length > 0))
        .map((r) => createEmptyEntity('Import', { ...r, residentState: r.residentState.toUpperCase().slice(0, 2) }))
      if (entities.length === 0) {
        setImportResult({ tone: 'warning', text: `No entity rows were found in ${files[0].name}.` })
        return
      }
      dispatch({ type: 'ADD_ENTITIES', entities })
      const needsAttention = entities.filter((e) => entityDetailsMissing(e).length > 0).length
      setShowIssues(true)
      setImportResult({
        tone: needsAttention > 0 ? 'warning' : 'success',
        text:
          `Imported ${entities.length} ${entities.length === 1 ? 'entity' : 'entities'} from ${files[0].name}.` +
          (needsAttention > 0 ? ` ${needsAttention} need attention — missing fields are highlighted below.` : ' All required fields are present.'),
      })
    } catch (err) {
      setImportResult({ tone: 'warning', text: err instanceof Error ? err.message : 'The file could not be read.' })
    }
  }

  return (
    <StepCard>
      <StepTitle subtitle="Please provide all business entities for which MISC will manage surplus lines tax reporting and compliance activities.">
        Business Entities Managed by MISC
      </StepTitle>

      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => dispatch({ type: 'ADD_ENTITIES', entities: [createEmptyEntity('Client')] })}
          >
            Add Entity
          </Button>
          <UploadButton label="Import File (CSV / XML)" accept=".csv,.xml,text/csv,application/xml,text/xml" onFiles={onImport} />
          <Button type="button" size="small" onClick={() => setShowIssues(true)}>
            Validate
          </Button>
          <Button
            type="button"
            size="small"
            onClick={() =>
              downloadCsvTemplate('business-entities-template.csv', [
                'Business Entity Name',
                'Resident Surplus Lines License State',
                'Surplus Lines License Number',
                'NPN',
                'FEIN',
              ])
            }
          >
            Download CSV template
          </Button>
        </Box>

        {importResult ? <Notice tone={importResult.tone}>{importResult.text}</Notice> : null}

        <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={headCellSx}>Business Entity Name</TableCell>
                <TableCell sx={headCellSx}>Resident SL License State</TableCell>
                <TableCell sx={headCellSx}>SL License Number</TableCell>
                <TableCell sx={headCellSx}>NPN</TableCell>
                <TableCell sx={headCellSx}>FEIN</TableCell>
                <TableCell sx={headCellSx}>Source</TableCell>
                <TableCell sx={headCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.entities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                    No business entities yet. Add an entity or import a file.
                  </TableCell>
                </TableRow>
              ) : null}
              {state.entities.map((e) => {
                const err = (value: string) => (showIssues || showBlockers) && value.trim().length === 0
                return (
                  <TableRow key={e.id}>
                    <TableCell sx={{ ...cellSx, minWidth: 240 }}>
                      <Field
                        value={e.name}
                        placeholder="Legal entity name"
                        error={err(e.name)}
                        inputProps={{ 'aria-label': 'Business entity name' }}
                        onChange={(ev) => update(e.id, { name: ev.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 170 }}>
                      <StateSelect
                        value={e.residentState}
                        error={err(e.residentState)}
                        inputProps={{ 'aria-label': 'Resident surplus lines license state' }}
                        onChange={(code) => update(e.id, { residentState: code })}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 150 }}>
                      <Field
                        value={e.slLicenseNumber}
                        error={err(e.slLicenseNumber)}
                        inputProps={{ 'aria-label': 'Surplus lines license number' }}
                        onChange={(ev) => update(e.id, { slLicenseNumber: ev.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 120 }}>
                      <Field
                        value={e.npn}
                        error={err(e.npn)}
                        inputProps={{ 'aria-label': 'NPN', inputMode: 'numeric' }}
                        onChange={(ev) => update(e.id, { npn: ev.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 130 }}>
                      <Field
                        value={e.fein}
                        placeholder="XX-XXXXXXX"
                        error={err(e.fein)}
                        inputProps={{ 'aria-label': 'FEIN' }}
                        onChange={(ev) => update(e.id, { fein: ev.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, pt: 1.75, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {SOURCE_LABELS[e.addedBy]}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Tooltip title="Remove entity">
                        <IconButton
                          aria-label={`Remove ${e.name || 'entity'}`}
                          onClick={() => setPendingRemoval(e)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>

        <ConfirmDialog
          open={pendingRemoval !== null}
          title="Remove entity?"
          confirmLabel="Remove"
          destructive
          onCancel={() => setPendingRemoval(null)}
          onConfirm={() => {
            if (pendingRemoval) dispatch({ type: 'REMOVE_ENTITY', entityId: pendingRemoval.id })
            setPendingRemoval(null)
          }}
        >
          Remove <strong>{pendingRemoval?.name || 'this entity'}</strong> and everything entered for it (states, DRLPs,
          individuals, and credentials)?
        </ConfirmDialog>

        <Notice tone="info">
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Information Notice</Typography>
          MISC will run a PDB report on each business entity listed. Submission of an entity indicates that MISC is
          expected to manage surplus lines reporting responsibilities for that entity.
        </Notice>
      </Stack>
    </StepCard>
  )
}
